import React, { Fragment } from "react"

import {
  AccordionComponent as Accordion,
  Summary,
  Details,
  Text,
} from "components/ui/Accordion"
import Table from "components/ui/Table"
import { STRING_TYPE } from "config"

const Content = () => {
  const HEADER = [
    {
      label: <strong>Name / content of the cookie</strong>,
      field: "name",
      format: STRING_TYPE,
      minWidth: 100,
    },
    {
      label: <strong>Cookie Description</strong>,
      field: "description",
      format: STRING_TYPE,
      minWidth: 100,
    },
  ]

  const ITEMS = [
    {
      name: "Session cookies",
      description:
        "Used for user authentication and authorization in self-service.",
    },
    {
      name: "Third-Party Cookies",
      description:
        "Analytical cookies collected regarding user self-service usage to improve user experience. More information: https://support.google.com/analytics/answer/11397207?hl=en&sjid=7820399061608210672-EU.",
    },
  ]

  return (
    <Fragment>
      <Accordion>
        <Summary>What are cookies?</Summary>
        <Details>
          <Text>
            Cookies are small text files consisting of letters and numbers that
            are downloaded and stored on your computer or other device (e.g.
            phone or tablet) when you visit a website. They may be used to store
            data you enter and remember preferences (e.g. language settings)
            while browsing the website or to collect feedback on which pages you
            visit.
          </Text>
        </Details>
      </Accordion>

      <Accordion>
        <Summary>What types of cookies does Fresh Finance use?</Summary>
        <Details>
          <Text>
            Fresh Finance uses several types of cookies that differ in their
            purpose and storage duration. Cookies are necessary to ensure the
            technical functionality of the website, facilitate its use, enhance
            security, and create a personalized experience. They enable us to
            provide the best self-service experience, gather insights into how
            customers use our website, and improve the services we offer.
          </Text>
          <Text>
            The storage duration determines how long cookies remain active in
            your browser.
          </Text>
          <Text>Fresh Finance uses three types of cookies:</Text>
          <Text>
            <ul>
              <li>
                <strong>Strictly necessary cookies</strong> Strictly necessary
                cookies allow you access to different parts of the Fresh Finance
                website. These cookies are stored on your computer, mobile
                phone, tablet, or other device while you visit the website.
                These cookies are essential for the website to function and
                cannot be disabled. Necessary cookies are processed based on
                legitimate interest, meaning that consent is not required for
                their use.
              </li>
              <li>
                <strong>Analytical Cookies</strong> Analytical cookies allow us
                to count visits and traffic sources, helping us measure and
                improve the performance of our website. They enable us to see
                how users navigate the website and which pages are visited most
                frequently. If you decline these cookies, your visit will not be
                reflected in our statistics.
              </li>
              <li>
                <strong>User Preference Cookies</strong> These cookies are used
                to enhance your experience on our website, provide relevant
                information about our products and services, and improve
                targeted marketing and advertising. These cookies may also be
                used for our advertising campaigns on third-party websites,
                allowing us to gain insights into our trusted partners' websites
                where you have encountered our ads. If you choose to decline
                these cookies, the ads displayed to you will be less
                personalized.
              </li>
            </ul>
          </Text>
        </Details>
      </Accordion>

      <Accordion>
        <Summary>Cookie Consent</Summary>
        <Details>
          <Text>
            For some cookies used for analysis and user preferences, we ask for
            your consent. We use these cookies to provide personalized
            information and enhance the performance of our website. If you
            consent to all cookies, click "Accept." If you want to choose which
            cookies you accept, click "Customize Cookies." You can give or
            withdraw your cookie consent and adjust your preferences via the
            pop-up window that appears when visiting the website.
          </Text>
          <Text>Your cookie preferences are stored for one year.</Text>
          <Text>
            By accepting a category, you consent to all cookies in that
            category. To provide the best user experience, Fresh Finance may
            update and modify the use of cookies, and therefore, the list may
            change accordingly.
          </Text>
        </Details>
      </Accordion>

      <Accordion>
        <Summary>Modifying or Withdrawing Cookie Preferences</Summary>
        <Details>
          <Text>
            You can adjust your preferences at any time to choose which cookies
            you allow us to use. You can also withdraw your consent at any time.
            Please note that deleting and/or blocking cookies may affect your
            user experience. It may impact browser functionality and the correct
            operation of self-service features.
          </Text>
        </Details>
      </Accordion>

      <Accordion>
        <Summary>Data Processing</Summary>
        <Details>
          <Text>
            If we process personal data related to cookie usage, it will be done
            in accordance with the <a href="/privacy">Privacy Notice</a>.
          </Text>
          <Text>As a user and data subject, you have the right to:</Text>
          <Text>
            <ul>
              <li>
                obtain confirmation if Fresh Finance processes your personal
                data and access such data;
              </li>
              <li>
                restrict and modify the scope of personal data processing;
              </li>
              <li>request the deletion of personal data;</li>
              <li>
                object to data processing if personal data is processed based on
                Fresh Finance's legitimate interest;
              </li>
              <li>
                withdraw your consent for personal data processing at any time;
              </li>
              <li>
                submit a complaint regarding personal data processing to the
                Estonian Data Protection Inspectorate (
                <a
                  href="https://www.aki.ee/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.aki.ee
                </a>
                ) if you believe that Fresh Finance's processing violates your
                rights and interests under data protection laws.
              </li>
            </ul>
          </Text>
          <Text>
            To exercise your rights, you can submit a request via self-service
            or send a digitally signed email inquiry to{" "}
            <a href="mailto:support@freshfinance.ee">support@freshfinance.ee</a>
            .
          </Text>
        </Details>
      </Accordion>

      <Accordion>
        <Summary>Contact Information</Summary>
        <Details>
          <Text>
            If you have any questions about the use and customization of
            cookies, withdrawing consent, or cookie-related matters, please
            contact us. Our contact details are available on the Fresh Finance
            website.
          </Text>
          <Text>
            You can also contact our data protection officer by sending an email
            to{" "}
            <a href="mailto:support@freshfinance.ee">support@freshfinance.ee</a>
            .
          </Text>
        </Details>
      </Accordion>

      <Accordion>
        <Summary>Validity of the Document</Summary>
        <Details>
          <Text>
            The Cookie Policy is prepared in Estonian and translated into
            English. In case of disputes, arguments, or issues related to
            language and interpretation, the legally binding version of this
            Cookie Policy is the Estonian version.
          </Text>
          <Text>
            The Cookie Policy is effective from February 18, 2025. Fresh Finance
            reserves the right to unilaterally amend this Cookie Policy at any
            time. Any changes will be published, and the latest version will be
            available at{" "}
            <a
              href="https://freshfinance.ee"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.freshfinance.ee
            </a>
            .
          </Text>
        </Details>
      </Accordion>

      <Accordion>
        <Summary>Currently Applied Cookies</Summary>
        <Details>
          <Table header={HEADER} items={ITEMS} fade={false} />
        </Details>
      </Accordion>
    </Fragment>
  )
}

export default Content
